import { EncapsulatedFeatureKeys } from "@lyfegen-platform/graphql-schema/models";
import { useDebounce } from "@lyfegen-platform/react-common";
import { parseDate } from "@lyfegen-platform/utils";
import { Box, Link, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useAgreementsPersistedFilters } from "../../components/filters/models";
import FlexTypographyComponent from "../../components/FlexTypographyComponent";
import FormattedCounterparties from "../../components/FormattedCounterparties";
import DashboardIcon from "../../components/icons/Controls/DashboardIcon";
import ListIcon from "../../components/icons/Controls/ListIcon";
import EncapsulatedFeature from "../../components/icons/EncapsulatedFeature";
import FlexTypographyArray from "../../components/LyfeTable/FlexTypographyArray";
import { ConfigurableTableType } from "../../components/LyfeTable/LyfeColumnsCard";
import LyfeDataTable from "../../components/LyfeTable/LyfeDataTable";
import LyfeToolbar from "../../components/LyfeTable/LyfeToolbar";
import TableBottomDrawer from "../../components/LyfeTable/TableBottomDrawer";
import MappedComponent from "../../components/mapped-components/MappedComponent";
import { Switch } from "../../components/Switch";
import { MS_UNTIL_QUERY_CALL } from "../../constants/util-constants";
import {
	AGREEMENTS_SUBSCRIPTION,
	GET_AGREEMENTS,
	GET_FILTERED_AGREEMENT_COUNT,
	UPDATE_AGREEMENTS_FILTER,
} from "../../containers/agreements/queries";
import SubscriptionForUpdates from "../../hooks/subscription-for-updates";
import { primaryActive, supportColor, tableIdMinWidth } from "../../hooks/use-map-theme-styles";
import { useDataMutation, useDataQuery } from "../../hooks/useDataModule";
import { HeaderActionOptions } from "../../models/common/data";
import { supportLink, supportLinkWithProtocol } from "../../models/common/modules";
import { ContractStatus } from "../../models/generated/graphql";
import { agreementsActivePage, agreementsRowsPerPage, pageSizeOptions } from "../../store/agreements";
import { bottomDrawerState, userOrganization } from "../../store/layout";
import {
	addSearchTermCondition,
	agreementsTableHeaderActionsState,
	filterStateFamily,
	getFieldNameByColumnName,
	getSortOrderByHeaderAction,
	TableColumns,
} from "../../store/lyfe-data-table";
import { userPersonalInfoState } from "../../store/user";
import { Column } from "../../types/tableTypes";
import renderCurrency from "../../utils/currency";
import { localeDate } from "../../utils/locale-date";
import startUpper from "../../utils/start-upper";
import { UPDATE_AGREEMENTS_TABLE_COLUMNS } from "../user/queries";
import { GetAgreementsQuery } from "./types/queries.generated";

export const defaultAgreementsSort = {
	column: "id",
	action: "sortDesc" as HeaderActionOptions,
};

export const separator = " , ";

export interface AgreementsViewProps {
	handleOnViewChange: () => void;
}

export function getTagTranslationKey(tagName: string): string {
	if (tagName === "Article 71") {
		return "ARTICLE_71";
	}
	return tagName.toUpperCase();
}

export function getAgreementTableColumns(): Array<TableColumns<keyof GetAgreementsQuery["agreementTableRows"][number]>> {
	return [
		{ title: "agreement-id", field: "id", default: true },
		{ title: "name", field: "name", default: true },
		{ title: "tags", field: "formattedTags", default: true },
		{ title: "product", field: "formattedProducts", default: true },
		{ title: "disease", field: "formattedDiseases", default: false },
		{ title: "indication", field: "formattedIndications", default: false },
		{ title: "counterparties", field: "formattedCounterparties", default: true },
		{ title: "days-to-expiry", field: "daysToExpiry", default: true },
		{ title: "validity-period", field: "formattedValidityPeriod", default: true },
		{ title: "status", field: "lastVersionStatus", default: true },
		{ title: "total-amount", field: "totalAmount", default: true },
	];
}

export function getFilterOptionsByFieldName(field: keyof GetAgreementsQuery["agreementTableRows"][number]) {
	switch (field) {
		case "status":
			return [ContractStatus.Approved, ContractStatus.InApproval, ContractStatus.Rejected, ContractStatus.Running];
		default:
			return [];
	}
}

export const agreementColumns = (t: any): Array<Column<GetAgreementsQuery["agreementTableRows"][number]>> => [
	{
		title: t("agreement-id"),
		field: "id",
		render: (rowData) => (
			<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
				{rowData.id}
			</Typography>
		),
	},
	{ title: startUpper(t("name")), field: "name" },
	{
		title: startUpper(t("status")),
		field: "lastVersionStatus",
		render: (rowData) => (
			<MappedComponent component="AgreementStatusComponent" props={{ ...rowData, status: rowData.lastVersionStatus }} />
		),
	},
	{
		title: startUpper(t("tags")),
		field: "formattedTags",
		render: (rowData) => (
			<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
				{t(getTagTranslationKey(rowData.formattedTags!))}
			</Typography>
		),
	},
	{
		title: startUpper(t("product")),
		field: "formattedProducts",
		render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="formattedProducts" />,
	},
	{
		title: startUpper(t("disease")),
		field: "formattedDiseases",
		render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="formattedDiseases" separator={separator} />,
	},
	{
		title: startUpper(t("indication")),
		field: "formattedIndications",
		render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="formattedIndications" separator={separator} />,
	},
	{
		title: startUpper(t("counterparties")),
		field: "formattedCounterparties",
		render: (rowData) => <FormattedCounterparties rowData={rowData} fieldAssessor="formattedCounterparties" />,
	},
	{
		title: startUpper(t("days-to-expiry")),
		field: "daysToExpiry",
		render: (rowData) => (
			<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
				{rowData.daysToExpiry}
			</Typography>
		),
	},
	{
		title: t("validity-period"),
		field: "formattedValidityPeriod",
		align: "right",
		render: (rowData) => (
			<FlexTypographyComponent
				containerStyles={{ justifyContent: "flex-end" }}
				text={`${localeDate(rowData.formattedValidityPeriod!.split(" - ")[0])}-${localeDate(
					parseDate(rowData.formattedValidityPeriod!.split(" - ")[1]),
					{ placeholder: "..." }
				)}`}
			/>
		),
	},
	{
		title: t("total-amount"),
		field: "totalAmount",
		align: "right",
		render: (rowData) => (
			<FlexTypographyComponent
				forceTooltipRender
				containerStyles={{ justifyContent: "flex-end" }}
				renderTooltipText={() =>
					renderCurrency(
						rowData.canUserViewFinancials ? rowData.totalAmount || 0 : "XX",
						(t(rowData.currencyName || "") || "") + ((rowData?.totalAmount || 0) !== 1 ? "s" : "")
					)
				}
				text={
					rowData.canUserViewFinancials
						? renderCurrency(rowData.totalAmount || 0, rowData.currencyCode)
						: `XX ${rowData.currencyCode}`
				}
			/>
		),
	},
];

const AgreementsTable: React.FC<AgreementsViewProps> = ({ handleOnViewChange }) => {
	const title = "agreements";
	const bottomDrawer = useSetRecoilState(bottomDrawerState);
	const { t } = useTranslation();
	const { push } = useHistory();
	const page = useRecoilValue(agreementsActivePage);
	const rowsPerPage = useRecoilValue(agreementsRowsPerPage);
	const [searchTerm, setSearchTerm] = useState<string>("*");
	const debouncedSearchTerm = useDebounce(searchTerm, MS_UNTIL_QUERY_CALL);
	const actionState = useRecoilValue(agreementsTableHeaderActionsState);
	const { loading: filtersLoading, filters: filtersInitialState, deserializedFilters } = useAgreementsPersistedFilters();
	const filters = useRecoilValue(filterStateFamily(title));
	const [user, setUser] = useRecoilState(userPersonalInfoState);
	const organization = useRecoilValue(userOrganization);
	const [update] = useDataMutation(UPDATE_AGREEMENTS_TABLE_COLUMNS);

	const isReportingEnabledByTier = organization.encapsulatedFeatures.some(
		(feature) => feature.isEnabled && feature.ofType.key === EncapsulatedFeatureKeys.CrossAgreementReporting
	);
	const { loading, data, error, refetch } = useDataQuery<GetAgreementsQuery>(GET_AGREEMENTS, {
		variables: {
			where: addSearchTermCondition(filters, debouncedSearchTerm),
			options: {
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				sort: {
					[getFieldNameByColumnName(actionState.column || defaultAgreementsSort.column).trimEnd()]:
						getSortOrderByHeaderAction(actionState.action || defaultAgreementsSort.action)?.trimEnd() || "DESC",
				},
			},
		},
		skip:
			!filters ||
			filters?.AND?.some((element: any) =>
				["timePeriod_GTE", "timePeriod_LTE", "invoiceTimePeriod_GTE", "invoiceTimePeriod_LTE"].includes(
					Object.keys(element)[0] as any
				)
			),
	});

	const filteredRecordsCount = data?.filteredAgreementCount[0]?._count;
	const totalRecordsCount = data?.allAgreementCount[0]?._count;

	function handleSearch(input: string) {
		setSearchTerm(input || "");
	}

	function handleRowClick(x: any, y: any, z: any, id: number) {
		push(`/agreements/agreement?id=${id}`);
	}

	useEffect(() => {
		bottomDrawer(
			<TableBottomDrawer
				title={title}
				totalRecords={totalRecordsCount || 0}
				filteredRecords={filteredRecordsCount || 0}
				pageSizeOptions={pageSizeOptions}
			/>
		);
		return () => bottomDrawer(null);
	}, [filteredRecordsCount, pageSizeOptions, filteredRecordsCount, totalRecordsCount]);

	useEffect(() => {
		const pendingColumns: Array<string> = filters?.AND?.map((filter: any) => Object.keys(filter)[0]).filter(Boolean);
		if (pendingColumns?.length) {
			const columns = [...user.agreementsTableColumns, ...pendingColumns];
			setUser({
				...user,
				agreementsTableColumns: columns,
			});
			update({ variables: { agreementsColumns: columns } });
		}
	}, [filters]);

	return (
		<div style={{ marginBottom: 28 }}>
			<SubscriptionForUpdates onCompleted={refetch} query={AGREEMENTS_SUBSCRIPTION} />
			<LyfeToolbar
				title={title}
				filterInitialState={filtersInitialState}
				filterPersistedState={deserializedFilters}
				saveFilterQuery={UPDATE_AGREEMENTS_FILTER}
				countQuery={GET_FILTERED_AGREEMENT_COUNT}
				buttons={[]}
				viewButtons={[
					<Box width="100%" display="flex" justifyContent={"space-between"}>
						<Switch
							isOn={false}
							onChange={handleOnViewChange}
							disabled={!isReportingEnabledByTier}
							iconOff={<ListIcon fill={primaryActive} />}
							iconOn={
								<>
									<DashboardIcon />
									{!isReportingEnabledByTier && <EncapsulatedFeature />}
								</>
							}
							tooltipPropsForIconOn={{
								placement: "right-start",
								interactive: true,
								title: !isReportingEnabledByTier ? (
									<Typography variant="body1">
										{t("cross-agreement-reporting-premium-feature")}
										<Link style={{ color: supportColor, cursor: "pointer" }} href={supportLinkWithProtocol}>
											{supportLink}
										</Link>
									</Typography>
								) : (
									""
								),
							}}
						/>
					</Box>,
				]}
				handleSearch={handleSearch}
				totalRecords={totalRecordsCount || 0}
				filteredRecords={filteredRecordsCount || 0}
				tableColumns={getAgreementTableColumns()}
				configurableTableType={ConfigurableTableType.AGREEMENTS}
			/>
			{error && <>Error...{console.error(error)}</>}
			{!error && (
				<LyfeDataTable
					title={title}
					hideRowHighlight
					onRowClickAction={handleRowClick}
					isLoading={loading || filtersLoading}
					data={data?.agreementTableRows || []}
					totalRecords={data?.allAgreementCount[0]._count ?? 0}
					filteredRecords={filteredRecordsCount}
					columns={agreementColumns(t).filter((col) => user.agreementsTableColumns.includes(col.field as string)) as any}
					toolbarProps={{ actionItems: [] }}
					pageSizeOptions={pageSizeOptions}
					page={page}
					defaultSort={defaultAgreementsSort}
					rowsPerPage={rowsPerPage}
				/>
			)}
		</div>
	);
};

export default AgreementsTable;
